import Company, { ErrorCompany } from 'requests/objects/company'
import ApiHandler from 'requests/apiHandler'
import Department from 'requests/objects/department'
import { setMessageBar } from 'redux/slices/common'
import store from 'redux/store'
import { MessageBarType } from '@fluentui/react'
import DeliveryTime from 'requests/objects/deliveryTime'
import Accessibility from 'requests/objects/accessibility'
import OperatingCenter from 'requests/objects/operatingCenter'
import VacationWeek from 'requests/objects/vacationWeek'
import VehicleEquipment from 'requests/objects/vehicleEquipment'

/**
 * CompaniesHandler
 * @augments {ApiHandler<Company, ErrorCompany>}
 */
export default class CompaniesHandler extends ApiHandler {
    constructor() {
        super({ type: Company, errorType: ErrorCompany, key: 'companies' })
    }

    /**
     * Get file from entity, require the folder name assiociated
     * @param {number} companyId companyId
     * @returns {import('requests/apiHandler').RequestApi<Company>} Request
     */
    setIsOperational(companyId) {
        const request = this.initFetchRequest({
            method: 'GET',
            url: [companyId, 'operational'],
        })

        return this.getRequestApi(
            () => request.fetchRequest.then(res => new Company(res.data.companies))
                .catch(err => { throw this.handleError(err) }),
            request.cancelToken,
        )
    }

    // -------------------------- OPERATING CENTERS --------------------------

    /**
     * Get all
     * @param {number} companyId companyId
     * @param {import('axios').AxiosRequestConfig['params']=} params Params
     * @returns {import('requests/apiHandler').RequestApi<OperatingCenter[]>} Request
     */
    getAllOperatingCenters(companyId, params = {}) {
        const request = this.initFetchRequest({ url: [companyId, 'operating-centers'], params })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => res.data['operating-centers']?.map(x => new OperatingCenter(x)) ?? [])
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }

    /**
     * Create
     * @param {number} companyId companyId
     * @param {OperatingCenter} obj Obj
     * @returns {import('requests/apiHandler').RequestApi<OperatingCenter>} Request
     */
    createOperatingCenter(companyId, obj = new OperatingCenter()) {
        const request = this.initFetchRequest({ url: [companyId, 'operating-centers'], method: 'POST', data: obj })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => {
                    store.dispatch(setMessageBar({ isDisplayed: true, type: MessageBarType.success, message: 'theElementHasBeenAdded' }))
                    return new OperatingCenter(res.data['operating-centers'])
                })
                .catch(err => {
                    throw this.handleError(err, false)
                }),
            request.cancelToken,
        )
    }

    /**
     * Update
     * @param {number} companyId companyId
     * @param {OperatingCenter} obj Obj
     * @param {number} id Id
     * @returns {import('requests/apiHandler').RequestApi<OperatingCenter>} Request
     */
    updateOperatingCenterById(companyId, obj = new OperatingCenter(), id = undefined) {
        const request = this.initFetchRequest({ url: [companyId, 'operating-centers', id], method: 'PUT', data: obj })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => {
                    store.dispatch(setMessageBar({ isDisplayed: true, type: MessageBarType.success, message: 'theElementhasBeenUpdated' }))
                    return new OperatingCenter(res.data['operating-centers'])
                })
                .catch(err => {
                    throw this.handleError(err, false)
                }),
            request.cancelToken,
        )
    }

    /**
     * Delete
     * @param {number} companyId companyId
     * @param {number} id id
     * @returns {import('requests/apiHandler').RequestApi<OperatingCenter>} Request
     */
    removeOperatingCenterById(companyId, id = undefined) {
        const request = this.initFetchRequest({ url: [companyId, 'operating-centers', id], method: 'DELETE' })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => {
                    store.dispatch(setMessageBar({ isDisplayed: true, type: MessageBarType.success, message: 'modalDeleteMessage' }))
                    return new OperatingCenter(res.data['operating-centers'])
                })
                .catch(err => {
                    throw this.handleError(err, false)
                }),
            request.cancelToken,
        )
    }

    /**
     * Upsert
     * @param {number} companyId companyId
     * @param {OperatingCenter} obj Request
     * @param {number=} id id
     * @returns {import('requests/apiHandler').RequestApi<OperatingCenter>} Request
     */
    // eslint-disable-next-line new-cap
    upsertOperatingCenter(companyId, obj = new OperatingCenter(), id = undefined) {
        if (id)
            return this.updateOperatingCenterById(companyId, obj, id)

        return this.createOperatingCenter(companyId, obj)
    }

    // -------------------------- DEPARTMENTS --------------------------

    /**
     * Get all
     * @param {number} companyId companyId
     * @param {import('axios').AxiosRequestConfig['params']=} params Params
     * @returns {import('requests/apiHandler').RequestApi<Department[]>} Request
     */
    getAllDepartments(companyId, params = {}) {
        const request = this.initFetchRequest({ url: [companyId, 'departments'], params })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => res.data.departments?.map(x => new Department(x)) ?? [])
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }

    /**
     * Create
     * @param {number} companyId companyId
     * @param {Department} obj Obj
     * @returns {import('requests/apiHandler').RequestApi<Department>} Request
     */
    createDepartment(companyId, obj = new Department()) {
        const request = this.initFetchRequest({ url: [companyId, 'departments'], method: 'POST', data: obj })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => {
                    store.dispatch(setMessageBar({ isDisplayed: true, type: MessageBarType.success, message: 'theElementHasBeenAdded' }))
                    return new Department(res.data.departments)
                })
                .catch(err => {
                    throw this.handleError(err, false)
                }),
            request.cancelToken,
        )
    }

    /**
     * Update
     * @param {number} companyId companyId
     * @param {Department} obj Obj
     * @param {number} id Id
     * @returns {import('requests/apiHandler').RequestApi<Department>} Request
     */
    updateDepartmentById(companyId, obj = new Department(), id = undefined) {
        const request = this.initFetchRequest({ url: [companyId, 'departments', id], method: 'PUT', data: obj })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => {
                    store.dispatch(setMessageBar({ isDisplayed: true, type: MessageBarType.success, message: 'theElementhasBeenUpdated' }))
                    return new Department(res.data.departments)
                })
                .catch(err => {
                    throw this.handleError(err, false)
                }),
            request.cancelToken,
        )
    }

    /**
     * Delete
     * @param {number} companyId companyId
     * @param {number} id id
     * @returns {import('requests/apiHandler').RequestApi<Department>} Request
     */
    removeDepartmentById(companyId, id = undefined) {
        const request = this.initFetchRequest({ url: [companyId, 'departments', id], method: 'DELETE' })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => {
                    store.dispatch(setMessageBar({ isDisplayed: true, type: MessageBarType.success, message: 'modalDeleteMessage' }))
                    return new Department(res.data.departments)
                })
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }

    /**
     * Upsert
     * @param {number} companyId companyId
     * @param {Department} obj Request
     * @param {number=} id id
     * @returns {import('requests/apiHandler').RequestApi<Department>} Request
     */
    // eslint-disable-next-line new-cap
    upsertDepartment(companyId, obj = new Department(), id = undefined) {
        if (id)
            return this.updateDepartmentById(companyId, obj, id)

        return this.createDepartment(companyId, obj)
    }

    // -------------------------- DELIVERY TIMES --------------------------

    /**
     * Get all
     * @param {number} companyId companyId
     * @param {import('axios').AxiosRequestConfig['params']=} params Params
     * @returns {import('requests/apiHandler').RequestApi<DeliveryTime[]>} Request
     */
    getAllDeliveryTimes(companyId, params = {}) {
        const request = this.initFetchRequest({ url: [companyId, 'delivery-times'], params })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => res.data['delivery-times']?.map(x => new DeliveryTime(x)) ?? [])
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }

    /**
     * Create
     * @param {number} companyId companyId
     * @param {DeliveryTime} obj Obj
     * @returns {import('requests/apiHandler').RequestApi<DeliveryTime>} Request
     */
    createDeliveryTime(companyId, obj = new DeliveryTime()) {
        const request = this.initFetchRequest({ url: [companyId, 'delivery-times'], method: 'POST', data: obj })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => {
                    store.dispatch(setMessageBar({ isDisplayed: true, type: MessageBarType.success, message: 'theElementHasBeenAdded' }))
                    return new DeliveryTime(res.data['delivery-times'])
                })
                .catch(err => {
                    throw this.handleError(err, false)
                }),
            request.cancelToken,
        )
    }

    /**
     * Update
     * @param {number} companyId companyId
     * @param {DeliveryTime} obj Obj
     * @param {number} id Id
     * @returns {import('requests/apiHandler').RequestApi<DeliveryTime>} Request
     */
    updateDeliveryTimeById(companyId, obj = new DeliveryTime(), id = undefined) {
        const request = this.initFetchRequest({ url: [companyId, 'delivery-times', id], method: 'PUT', data: obj })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => {
                    store.dispatch(setMessageBar({ isDisplayed: true, type: MessageBarType.success, message: 'theElementhasBeenUpdated' }))
                    return new DeliveryTime(res.data['delivery-times'])
                })
                .catch(err => {
                    throw this.handleError(err, false)
                }),
            request.cancelToken,
        )
    }

    /**
     * Delete
     * @param {number} companyId companyId
     * @param {number} id id
     * @returns {import('requests/apiHandler').RequestApi<DeliveryTime>} Request
     */
    removeDeliveryTimeById(companyId, id = undefined) {
        const request = this.initFetchRequest({ url: [companyId, 'delivery-times', id], method: 'DELETE' })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => {
                    store.dispatch(setMessageBar({ isDisplayed: true, type: MessageBarType.success, message: 'modalDeleteMessage' }))
                    return new DeliveryTime(res.data['delivery-times'])
                })
                .catch(err => {
                    throw this.handleError(err, false)
                }),
            request.cancelToken,
        )
    }

    /**
     * Upsert
     * @param {number} companyId companyId
     * @param {DeliveryTime} obj Request
     * @param {number=} id id
     * @returns {import('requests/apiHandler').RequestApi<DeliveryTime>} Request
     */
    // eslint-disable-next-line new-cap
    upsertDeliveryTime(companyId, obj = new DeliveryTime(), id = undefined) {
        if (id)
            return this.updateDeliveryTimeById(companyId, obj, id)

        return this.createDeliveryTime(companyId, obj)
    }

    /**
     * Import
     * @param {globalThis.File} file file
     * @param {number} companyId companyId
     * @returns {import('requests/apiHandler').RequestApi<DeliveryTime[]>} Returns
     */
    importDeliveryTimes(file, companyId) {
        const formData = new FormData()
        formData.append('file', file)

        const request = this.initFetchRequest({
            url: [companyId, 'delivery-times', 'import'],
            method: 'POST',
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data;',
            },
        })
        return this.getRequestApi(
            () => request.fetchRequest
                // eslint-disable-next-line new-cap
                .then(res => {
                    store.dispatch(setMessageBar({ isDisplayed: true, type: MessageBarType.success, message: 'dataIntegrationCompleted' }))
                    return res.data['delivery-times']?.map(a => new DeliveryTime(a)) ?? []
                })
                .catch(err => {
                    throw this.handleError(err, false)
                }),
            request.cancelToken,
        )
    }

    /**
     * Download template
     * @param {number} companyId companyId
     * @returns {import('requests/apiHandler').RequestApi<Blob>} Request
     */
    downloadDeliveryTimeTemplate(companyId) {
        const request = this.initFetchRequest({
            method: 'GET',
            url: [companyId, 'delivery-times', 'import', 'template'],
            responseType: 'arraybuffer',
        })

        return this.getRequestApi(
            () => request.fetchRequest
                // eslint-disable-next-line new-cap
                .then(res => new Blob([res.data]))
                .catch(err => {
                    throw this.handleError(err, false)
                }),
            request.cancelToken,
        )
    }

    /**
     * Import importDepartments
     * @param {globalThis.File} file file
     * @param {number} companyId companyId
     * @returns {import('requests/apiHandler').RequestApi<Department[]>} Returns
     */
    importDepartments(file, companyId) {
        const formData = new FormData()
        formData.append('file', file)

        const request = this.initFetchRequest({
            url: [companyId, 'departments', 'import'],
            method: 'POST',
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data;',
            },
        })
        return this.getRequestApi(
            () => request.fetchRequest
                // eslint-disable-next-line new-cap
                .then(res => {
                    store.dispatch(setMessageBar({ isDisplayed: true, type: MessageBarType.success, message: 'dataIntegrationCompleted' }))
                    return res.data.departments?.map(a => new Department(a)) ?? []
                })
                .catch(err => {
                    throw this.handleError(err, false)
                }),
            request.cancelToken,
        )
    }

    /**
     * Download template
     * @param {number} companyId companyId
     * @returns {import('requests/apiHandler').RequestApi<Blob>} Request
     */
    downloadDepartmentsTemplate(companyId) {
        const request = this.initFetchRequest({
            method: 'GET',
            url: [companyId, 'departments', 'import', 'template'],
            responseType: 'arraybuffer',
        })

        return this.getRequestApi(
            () => request.fetchRequest
                // eslint-disable-next-line new-cap
                .then(res => new Blob([res.data]))
                .catch(err => {
                    throw this.handleError(err, false)
                }),
            request.cancelToken,
        )
    }

    /**
     * downloadDepartmentsExample
     * @param {number} companyId companyId
     * @returns {import('requests/apiHandler').RequestApi<Blob>} Request
     */
    downloadDepartmentsExample(companyId) {
        const request = this.initFetchRequest({
            method: 'GET',
            url: [companyId, 'departments', 'import', 'example'],
            responseType: 'arraybuffer',
        })

        return this.getRequestApi(
            () => request.fetchRequest
                // eslint-disable-next-line new-cap
                .then(res => new Blob([res.data]))
                .catch(err => {
                    throw this.handleError(err, false)
                }),
            request.cancelToken,
        )
    }

    /**
     * Get all
     * @param {number} companyId companyId
     * @param {import('axios').AxiosRequestConfig['params']=} params Params
     * @returns {import('requests/apiHandler').RequestApi<Accessibility[]>} Request
     */
    getAllAccessibilities(companyId, params = {}) {
        const request = this.initFetchRequest({ url: [companyId, 'accessibilities'], params })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => res.data.accessibilities?.map(x => new Accessibility(x)) ?? [])
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }

    /**
     * Create
     * @param {number} companyId companyId
     * @param {Accessibility} obj Obj
     * @returns {import('requests/apiHandler').RequestApi<Accessibility>} Request
     */
    createAccessibility(companyId, obj = new Accessibility()) {
        const request = this.initFetchRequest({ url: [companyId, 'accessibilities'], method: 'POST', data: obj })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => {
                    store.dispatch(setMessageBar({ isDisplayed: true, type: MessageBarType.success, message: 'theElementHasBeenAdded' }))
                    return new Accessibility(res.data.accessibilities)
                })
                .catch(err => {
                    throw this.handleError(err, false)
                }),
            request.cancelToken,
        )
    }

    /**
     * Update
     * @param {number} companyId companyId
     * @param {Accessibility} obj Obj
     * @param {number} id Id
     * @returns {import('requests/apiHandler').RequestApi<Accessibility>} Request
     */
    updateAccessibilityById(companyId, obj = new Accessibility(), id = undefined) {
        const request = this.initFetchRequest({ url: [companyId, 'accessibilities', id], method: 'PUT', data: obj })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => {
                    store.dispatch(setMessageBar({ isDisplayed: true, type: MessageBarType.success, message: 'theElementhasBeenUpdated' }))
                    return new Accessibility(res.data.accessibilities)
                })
                .catch(err => {
                    throw this.handleError(err, false)
                }),
            request.cancelToken,
        )
    }

    /**
     * Delete
     * @param {number} companyId companyId
     * @param {number} id id
     * @returns {import('requests/apiHandler').RequestApi<Accessibility>} Request
     */
    removeAccessibilityById(companyId, id = undefined) {
        const request = this.initFetchRequest({ url: [companyId, 'accessibilities', id], method: 'DELETE' })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => {
                    store.dispatch(setMessageBar({ isDisplayed: true, type: MessageBarType.success, message: 'modalDeleteMessage' }))
                    return new Accessibility(res.data.accessibilities)
                })
                .catch(err => {
                    throw this.handleError(err, false)
                }),
            request.cancelToken,
        )
    }

    /**
     * Upsert
     * @param {number} companyId companyId
     * @param {Accessibility} obj Request
     * @param {number=} id id
     * @returns {import('requests/apiHandler').RequestApi<Accessibility>} Request
     */
    // eslint-disable-next-line new-cap
    upsertAccessibility(companyId, obj = new Accessibility(), id = undefined) {
        if (id)
            return this.updateAccessibilityById(companyId, obj, id)

        return this.createAccessibility(companyId, obj)
    }

    /**
     * Get all
     * @param {number} companyId companyId
     * @param {import('axios').AxiosRequestConfig['params']=} params Params
     * @returns {import('requests/apiHandler').RequestApi<VacationWeek[]>} Request
     */
    getAllVacationWeeks(companyId, params = {}) {
        const request = this.initFetchRequest({ url: [companyId, 'vacation-weeks'], params })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => res.data['vacation-weeks']?.map(x => new VacationWeek(x)) ?? [])
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }

    /**
     * Create
     * @param {number} companyId companyId
     * @param {VacationWeek} obj Obj
     * @returns {import('requests/apiHandler').RequestApi<VacationWeek>} Request
     */
    createVacationWeek(companyId, obj = new VacationWeek()) {
        const request = this.initFetchRequest({ url: [companyId, 'vacation-weeks'], method: 'POST', data: obj })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => {
                    store.dispatch(setMessageBar({ isDisplayed: true, type: MessageBarType.success, message: 'theElementHasBeenAdded' }))
                    return new VacationWeek(res.data['vacation-weeks'])
                })
                .catch(err => {
                    throw this.handleError(err, false)
                }),
            request.cancelToken,
        )
    }

    /**
     * Update
     * @param {number} companyId companyId
     * @param {VacationWeek} obj Obj
     * @param {number} id Id
     * @returns {import('requests/apiHandler').RequestApi<VacationWeek>} Request
     */
    updateVacationWeekById(companyId, obj = new VacationWeek(), id = undefined) {
        const request = this.initFetchRequest({ url: [companyId, 'vacation-weeks', id], method: 'PUT', data: obj })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => {
                    store.dispatch(setMessageBar({ isDisplayed: true, type: MessageBarType.success, message: 'theElementhasBeenUpdated' }))
                    return new VacationWeek(res.data['vacation-weeks'])
                })
                .catch(err => {
                    throw this.handleError(err, false)
                }),
            request.cancelToken,
        )
    }

    /**
     * Delete
     * @param {number} companyId companyId
     * @param {number} id id
     * @returns {import('requests/apiHandler').RequestApi<VacationWeek>} Request
     */
    removeVacationWeekById(companyId, id = undefined) {
        const request = this.initFetchRequest({ url: [companyId, 'vacation-weeks', id], method: 'DELETE' })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => {
                    store.dispatch(setMessageBar({ isDisplayed: true, type: MessageBarType.success, message: 'modalDeleteMessage' }))
                    return new VacationWeek(res.data['vacation-weeks'])
                })
                .catch(err => {
                    throw this.handleError(err, false)
                }),
            request.cancelToken,
        )
    }

    /**
     * Upsert
     * @param {number} companyId companyId
     * @param {VacationWeek} obj Request
     * @param {number=} id id
     * @returns {import('requests/apiHandler').RequestApi<VacationWeek>} Request
     */
    // eslint-disable-next-line new-cap
    upsertVacationWeek(companyId, obj = new VacationWeek(), id = undefined) {
        if (id)
            return this.updateVacationWeekById(companyId, obj, id)

        return this.createVacationWeek(companyId, obj)
    }
    // -------------------------- VEHICLE EQUIPMENTS --------------------------

    /**
     * Get all
     * @param {number} companyId companyId
     * @param {import('axios').AxiosRequestConfig['params']=} params Params
     * @returns {import('requests/apiHandler').RequestApi<VehicleEquipment[]>} Request
     */
    getAllVehicleEquipments(companyId, params = {}) {
        const request = this.initFetchRequest({ url: [companyId, 'vehicle-equipments'], params })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => res.data.vehicleEquipments?.map(x => new VehicleEquipment(x)) ?? [])
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }

    /**
     * Create
     * @param {number} companyId companyId
     * @param {VehicleEquipment} obj Obj
     * @returns {import('requests/apiHandler').RequestApi<VehicleEquipment>} Request
     */
    createVehicleEquipment(companyId, obj = new VehicleEquipment()) {
        const request = this.initFetchRequest({ url: [companyId, 'vehicle-equipments'], method: 'POST', data: obj })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => {
                    store.dispatch(setMessageBar({ isDisplayed: true, type: MessageBarType.success, message: 'theElementHasBeenAdded' }))
                    return new VehicleEquipment(res.data.vehicleEquipments)
                })
                .catch(err => {
                    throw this.handleError(err, false)
                }),
            request.cancelToken,
        )
    }

    /**
     * Update
     * @param {number} companyId companyId
     * @param {VehicleEquipment} obj Obj
     * @param {number} id Id
     * @returns {import('requests/apiHandler').RequestApi<VehicleEquipment>} Request
     */
    updateVehicleEquipmentById(companyId, obj = new VehicleEquipment(), id = undefined) {
        const request = this.initFetchRequest({ url: [companyId, 'vehicle-equipments', id], method: 'PUT', data: obj })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => {
                    store.dispatch(setMessageBar({ isDisplayed: true, type: MessageBarType.success, message: 'theElementhasBeenUpdated' }))
                    return new VehicleEquipment(res.data.vehicleEquipments)
                })
                .catch(err => {
                    throw this.handleError(err, false)
                }),
            request.cancelToken,
        )
    }

    /**
     * Delete
     * @param {number} companyId companyId
     * @param {number} id id
     * @returns {import('requests/apiHandler').RequestApi<VehicleEquipment>} Request
     */
    removeVehicleEquipmentById(companyId, id = undefined) {
        const request = this.initFetchRequest({ url: [companyId, 'vehicle-equipments', id], method: 'DELETE' })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => {
                    store.dispatch(setMessageBar({ isDisplayed: true, type: MessageBarType.success, message: 'modalDeleteMessage' }))
                    return new VehicleEquipment(res.data.vehicleEquipments)
                })
                .catch(err => {
                    throw this.handleError(err, false)
                }),
            request.cancelToken,
        )
    }

    /**
     * Upsert
     * @param {number} companyId companyId
     * @param {VehicleEquipment} obj Request
     * @param {number=} id id
     * @returns {import('requests/apiHandler').RequestApi<VehicleEquipment>} Request
     */
    upsertVehicleEquipment(companyId, obj = new VehicleEquipment(), id = undefined) {
        if (id)
            return this.updateVehicleEquipmentById(companyId, obj, id)

        return this.createVehicleEquipment(companyId, obj)
    }
}
